import { Box, Card, Divider, Stack, Typography } from '@mui/material';

function formatMetric(value) {
  if (value == null || value === '') {
    return '—';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return String(value);
  }
  return Number.isInteger(number) ? String(number) : number.toFixed(1);
}

function formatAcwr(value) {
  if (value == null || value === '') {
    return '—';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return String(value);
  }
  return number.toFixed(2);
}

function formatPhaseRange(phase) {
  const min = phase.min_ratio;
  const max = phase.max_ratio;
  const maxInclusive = phase.max_inclusive;

  if (min == null && max != null) {
    return maxInclusive ? `≤ ${max}` : `< ${max}`;
  }
  if (min != null && max == null) {
    return `≥ ${min}`;
  }
  if (min != null && max != null) {
    return maxInclusive ? `${min} – ${max}` : `${min} – ${max}`;
  }
  return '—';
}

function phaseTitleFromKey(currentPhase, thresholds) {
  const match = (thresholds || []).find((item) => item.key === currentPhase);
  if (match?.label) {
    return match.label;
  }
  if (!currentPhase) {
    return 'Shield phase';
  }
  return currentPhase.replace(/_/g, ' ');
}

function MetricRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.45 }}>
      <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>{label}</Typography>
      <Typography sx={{ color: '#F3F4F6', fontWeight: 600, fontSize: '0.8rem' }}>
        {formatMetric(value)}
      </Typography>
    </Stack>
  );
}

const cardSx = {
  bgcolor: '#151921',
  border: '1px solid #232935',
  borderRadius: 2
};

export default function PlayerInsightsSummary({ insights, workload, playerName }) {
  const thresholds = insights?.phase_thresholds || [];
  const currentPhase = insights?.current_phase;
  const phaseTitle = phaseTitleFromKey(currentPhase, thresholds);
  const statusText = insights?.baseline_status || '—';
  const latestDailyLoad =
    workload?.latest_daily_load ?? workload?.latestDailyLoad ?? insights?.latest_session_stress_output;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ color: '#F3F4F6', fontWeight: 700 }}>
          Insights
        </Typography>
        <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
          {playerName ? `${playerName}'s shield in context` : 'Your shield in context'}
        </Typography>
      </Box>

      <Card sx={cardSx} elevation={0}>
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              color: '#3F90DF',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              mb: 1
            }}
          >
            {phaseTitle.toUpperCase()}
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em' }}>
            ACWR RATIO
          </Typography>
          <Typography sx={{ color: '#F3F4F6', fontWeight: 800, fontSize: '2rem', lineHeight: 1.1, my: 0.5 }}>
            {formatAcwr(insights?.workload_balance_ratio)}
          </Typography>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', lineHeight: 1.5, mb: 1.5 }}>
            {statusText}
          </Typography>
          <Divider sx={{ borderColor: '#232935', mb: 0.5 }} />
          <MetricRow label="Acute fatigue" value={insights?.acute_system_stress} />
          <MetricRow label="Chronic fitness" value={insights?.chronic_foundation_engine} />
          <MetricRow label="Latest daily load" value={latestDailyLoad} />
        </Box>
      </Card>

      <Box>
        <Typography
          sx={{
            color: '#6B7280',
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            mb: 1
          }}
        >
          SHIELD TRAINING PHASES
        </Typography>
        <Stack spacing={1}>
          {thresholds.map((phase) => {
            const active = phase.key === currentPhase;
            return (
              <Card
                key={phase.key}
                sx={{
                  ...cardSx,
                  borderColor: active ? '#3F90DF' : '#232935',
                  boxShadow: active ? '0 0 0 1px rgba(63, 144, 223, 0.35)' : 'none'
                }}
                elevation={0}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 1.5, py: 1.1 }}
                >
                  <Typography
                    sx={{
                      color: active ? '#F3F4F6' : '#9CA3AF',
                      fontSize: '0.78rem',
                      fontWeight: active ? 600 : 400
                    }}
                  >
                    {phase.label}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>
                    {formatPhaseRange(phase)}
                  </Typography>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Box>

      <Card sx={cardSx} elevation={0}>
        <Box sx={{ p: 2 }}>
          <Typography sx={{ color: '#3F90DF', fontSize: '0.75rem', fontWeight: 700, mb: 1 }}>
            [{phaseTitle}]
          </Typography>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', lineHeight: 1.55 }}>
            {statusText}
          </Typography>
        </Box>
      </Card>

      <Card sx={cardSx} elevation={0}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography
            sx={{
              color: '#6B7280',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.08em'
            }}
          >
            AVG EFFORT
          </Typography>
          <Typography sx={{ color: '#F3F4F6', fontWeight: 800, fontSize: '2rem', lineHeight: 1.2, my: 0.5 }}>
            {formatMetric(insights?.average_effort_last_5_sessions)}
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>Last 5 sessions</Typography>
        </Box>
      </Card>
    </Stack>
  );
}
